import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { readImageFileAsDataUri, SUPPORTED_READING_IMAGE_ACCEPT } from '../../../helper/ImageFileHelper';
import { notificationActions } from '../../../redux/notification';

export type ImagePickerHandle = { open: () => void };

interface ImagePickerProps {
    onChange: (imageBase64: string) => void;
    onOpenChange: (open: boolean) => void;
    onDecodeStart?: () => void;
    onDecodeFailed?: () => void;
    capture?: React.InputHTMLAttributes<HTMLInputElement>['capture'];
    accept?: string;
}

const ImagePicker = forwardRef<ImagePickerHandle, ImagePickerProps>(({ onChange, onOpenChange, onDecodeStart, onDecodeFailed, capture, accept = SUPPORTED_READING_IMAGE_ACCEPT }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const isMountedRef = useRef(true);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        // Set again on every mount: React 18 StrictMode runs mount → cleanup → mount in development.
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            open: () => {
                onOpenChange(true);
                inputRef.current?.click();
            },
        }),
        [onOpenChange],
    );

    return (
        <input
            aria-hidden={true}
            ref={inputRef}
            onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                const input = e.currentTarget;
                const resetPicker = () => {
                    if (!isMountedRef.current) {
                        return;
                    }

                    input.value = '';
                    onOpenChange(false);
                };

                if (!file) {
                    resetPicker();
                    return;
                }

                onDecodeStart?.();

                readImageFileAsDataUri(file)
                    .then((res) => onChange(res))
                    .catch((err) => {
                        onDecodeFailed?.();
                        console.log('Error in ImagePicker: ', err);
                        dispatch(
                            notificationActions.showErrorNotification(
                                t('jobs.details.notifications.imageFormatUnsupported.title'),
                                t('jobs.details.notifications.imageFormatUnsupported.message'),
                            ),
                        );
                    })
                    .finally(resetPicker);
            }}
            type={'file'}
            accept={accept}
            capture={capture}
            style={{ display: 'none' }}
        />
    );
});

export default ImagePicker;
